import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { FaTag, FaPlus, FaTrash, FaEdit, FaCheck, FaTimes, FaChartPie, FaCalendarAlt, FaUsers } from 'react-icons/fa';
import { useToast } from '../../context/ToastContext';
import PageHeader from '../../components/ui/PageHeader';
import SkeletonLoader from '../../components/ui/SkeletonLoader';
import { EmptyTableRow } from '../../components/ui/EmptyState';
import PageLayout from '../../components/ui/PageLayout';
import DataTable from '../../components/ui/DataTable';

const PromoCodeManagement = () => {
    const toast = useToast();
    const [promoCodes, setPromoCodes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCode, setEditingCode] = useState(null);
    const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
    const [selectedCodeDetails, setSelectedCodeDetails] = useState(null);
    const [formData, setFormData] = useState({
        code: '',
        discountType: 'percentage',
        discountValue: '',
        applicableFee: 'Our Fee',
        applicableService: 'All',
        maxUses: '',
        validUntil: '',
        description: '',
        isActive: true
    });

    const fetchPromoCodes = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/promo-codes/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setPromoCodes(response.data.promoCodes);
            }
        } catch (error) {
            console.error('Error fetching promo codes:', error);
            toast.error('Failed to fetch promo codes');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPromoCodes();
    }, []);

    const handleOpenModal = (code = null) => {
        if (code) {
            setEditingCode(code);
            setFormData({
                code: code.code,
                discountType: code.discountType || 'percentage',
                discountValue: code.discountValue,
                applicableFee: code.applicableFee,
                applicableService: code.applicableService,
                maxUses: code.maxUses || '',
                validUntil: code.validUntil ? new Date(code.validUntil).toISOString().slice(0, 16) : '',
                description: code.description || '',
                isActive: code.isActive !== undefined ? code.isActive : true
            });
        } else {
            setEditingCode(null);
            setFormData({
                code: '',
                discountType: 'percentage',
                discountValue: '',
                applicableFee: 'Our Fee',
                applicableService: 'All',
                maxUses: '',
                validUntil: '',
                description: '',
                isActive: true
            });
        }
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setEditingCode(null);
    };

    const handleViewDetails = (code) => {
        setSelectedCodeDetails(code);
        setIsDetailsModalOpen(true);
    };

    const handleCloseDetailsModal = () => {
        setIsDetailsModalOpen(false);
        setSelectedCodeDetails(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('adminToken');
            const data = { ...formData };
            if (data.maxUses === '') data.maxUses = null;

            if (editingCode) {
                const response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/promo-codes/${editingCode._id}`, data, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (response.data.success) {
                    toast.success('Promo code updated');
                    fetchPromoCodes();
                    handleCloseModal();
                }
            } else {
                const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/promo-codes/create`, data, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (response.data.success) {
                    toast.success('Promo code created');
                    fetchPromoCodes();
                    handleCloseModal();
                }
            }
        } catch (error) {
            console.error('Error saving promo code:', error);
            toast.error(error.response?.data?.message || 'Failed to save promo code');
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this promo code?')) {
            try {
                const token = localStorage.getItem('adminToken');
                await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/promo-codes/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Promo code deleted');
                fetchPromoCodes();
            } catch (error) {
                console.error('Error deleting promo code:', error);
                toast.error('Failed to delete promo code');
            }
        }
    };

    const toggleStatus = async (code) => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/api/promo-codes/${code._id}`, 
                { isActive: !code.isActive }, 
                { headers: { Authorization: `Bearer ${token}` } }
            );
            if (response.data.success) {
                toast.success(`Promo code ${!code.isActive ? 'enabled' : 'disabled'}`);
                fetchPromoCodes();
            }
        } catch (error) {
            console.error('Error toggling status:', error);
            toast.error('Failed to update status');
        }
    };

    return (
        <PageLayout>
            <PageHeader
                title="Promo Code Management"
                description="Create and manage discounts for your users"
                icon={FaTag}
                actions={
                    <button onClick={() => handleOpenModal()} className="btn-premium flex items-center gap-2">
                        <FaPlus className="w-4 h-4" /> Create New Code
                    </button>
                }
            />

            {loading ? (
                <SkeletonLoader variant="table" rows={5} cols={6} />
            ) : (
                <div className="grid grid-cols-1 gap-6">
                    <DataTable>
                        <table className="w-full text-left">
                            <thead className="bg-gradient-to-r from-primary-600 to-primary-700 text-white">
                                <tr>
                                    <th className="px-4 py-4 text-sm font-semibold uppercase tracking-wider text-white">Code</th>
                                    <th className="px-4 py-4 text-sm font-semibold uppercase tracking-wider text-white">Discount</th>
                                    <th className="px-4 py-4 text-sm font-semibold uppercase tracking-wider text-white">Statistics (Uses/Limit)</th>
                                    <th className="px-4 py-4 text-sm font-semibold uppercase tracking-wider text-white">Expiry</th>
                                    <th className="px-4 py-4 text-sm font-semibold uppercase tracking-wider text-white">Status</th>
                                    <th className="px-4 py-4 text-sm font-semibold uppercase tracking-wider text-right text-white">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-dark-700">
                                {promoCodes.length === 0 ? (
                                    <EmptyTableRow
                                        colSpan={6}
                                        preset="create"
                                        title="No Promo Codes Yet"
                                        description="Create your first promo code to offer discounts to users."
                                    />
                                ) : (
                                    promoCodes.map((code) => (
                                        <tr key={code._id} className="hover:bg-gray-50 dark:hover:bg-dark-700/50 transition-colors">
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-3">
                                                    <div className="p-2 bg-primary-100 dark:bg-primary-900/30 text-primary-600 rounded-lg">
                                                        <FaTag size={16} />
                                                    </div>
                                                    <div>
                                                        <div className="font-bold dark:text-white uppercase">{code.code}</div>
                                                        <div className="text-xs text-gray-500 dark:text-gray-400">{code.description}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <span className="px-2.5 py-1 text-sm font-medium bg-green-100 dark:bg-green-900/30 text-green-600 rounded-full">
                                                    {code.discountType === 'fixed' ? '$' : ''}{code.discountValue}{code.discountType === 'percentage' ? '%' : ''} off
                                                </span>
                                                <div className="text-xs text-gray-500 mt-1">{code.applicableFee}</div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex flex-col gap-2">
                                                    <div className="flex items-center gap-2 text-sm dark:text-gray-300">
                                                        <FaUsers className="text-gray-400" size={14} />
                                                        <span>Applied: <strong>{code.totalApplied || 0}</strong></span>
                                                    </div>
                                                    <div className="flex items-center gap-2 text-sm dark:text-gray-300">
                                                        <FaCheck className="text-green-500" size={14} />
                                                        <span>Converted: <strong>{code.currentUses || 0}</strong></span>
                                                        <span className="text-xs text-gray-500">/ {code.maxUses || '∞'}</span>
                                                    </div>
                                                    {code.maxUses && (
                                                        <div className="w-full bg-gray-200 dark:bg-dark-600 h-1.5 rounded-full mt-1">
                                                            <div 
                                                                className={`h-full rounded-full ${code.currentUses / code.maxUses > 0.8 ? 'bg-red-500' : 'bg-primary-500'}`}
                                                                style={{ width: `${Math.min((code.currentUses / code.maxUses) * 100, 100)}%` }}
                                                            ></div>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2 text-sm dark:text-gray-300">
                                                    <FaCalendarAlt className="text-gray-400" size={14} />
                                                    <span>{new Date(code.validUntil).toLocaleDateString()} {new Date(code.validUntil).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                                                </div>
                                                {new Date() > new Date(code.validUntil) && (
                                                    <span className="text-[10px] text-red-500 font-bold uppercase">Expired</span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <button 
                                                    onClick={() => toggleStatus(code)}
                                                    className={`px-3 py-1 text-xs font-semibold rounded-full transition-colors ${
                                                        code.isActive 
                                                        ? 'bg-blue-100 text-blue-600 hover:bg-blue-200' 
                                                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                    }`}
                                                >
                                                    {code.isActive ? 'Active' : 'Disabled'}
                                                </button>
                                            </td>
                                            <td className="px-4 py-3 text-right">
                                                <div className="flex justify-end gap-2">
                                                    <button 
                                                        onClick={() => handleViewDetails(code)} 
                                                        className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                                                        title="View Details"
                                                    >
                                                        <FaChartPie size={16} />
                                                    </button>
                                                    <button onClick={() => handleOpenModal(code)} className="p-2 text-gray-400 hover:text-primary-600 transition-colors">
                                                        <FaEdit size={16} />
                                                    </button>
                                                    <button onClick={() => handleDelete(code._id)} className="p-2 text-gray-400 hover:text-red-600 transition-colors">
                                                        <FaTrash size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </DataTable>
                </div>
            )}

            {/* Modal */}
            {isModalOpen && createPortal(
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" onClick={handleCloseModal}>
                    <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-scale-in my-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="p-6 border-b border-gray-100 dark:border-dark-700 flex justify-between items-center">
                            <h3 className="text-xl font-bold dark:text-white">
                                {editingCode ? 'Edit Promo Code' : 'Create Promo Code'}
                            </h3>
                            <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600">
                                <FaTimes size={20} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Promo Code</label>
                                    <input 
                                        type="text" 
                                        required 
                                        className="w-full p-2.5 rounded-lg border dark:bg-dark-700 dark:border-dark-600 dark:text-white uppercase"
                                        placeholder="E.G. SAVE10"
                                        value={formData.code}
                                        onChange={(e) => setFormData({...formData, code: e.target.value})}
                                        disabled={!!editingCode}
                                    />
                                </div>
                                <div className="col-span-1">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Discount Type</label>
                                    <select 
                                        className="w-full p-2.5 rounded-lg border dark:bg-dark-700 dark:border-dark-600 dark:text-white"
                                        value={formData.discountType}
                                        onChange={(e) => setFormData({...formData, discountType: e.target.value})}
                                    >
                                        <option value="percentage">Percentage (%)</option>
                                        <option value="fixed">Fixed Amount ($)</option>
                                    </select>
                                </div>
                                <div className="col-span-1">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Value</label>
                                    <input 
                                        type="number" 
                                        required 
                                        className="w-full p-2.5 rounded-lg border dark:bg-dark-700 dark:border-dark-600 dark:text-white"
                                        placeholder="10"
                                        value={formData.discountValue}
                                        onChange={(e) => setFormData({...formData, discountValue: e.target.value})}
                                    />
                                </div>
                                <div className="col-span-1">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max Usage</label>
                                    <input 
                                        type="number" 
                                        className="w-full p-2.5 rounded-lg border dark:bg-dark-700 dark:border-dark-600 dark:text-white"
                                        placeholder="Unlimited if empty"
                                        value={formData.maxUses}
                                        onChange={(e) => setFormData({...formData, maxUses: e.target.value})}
                                    />
                                </div>
                                <div className="col-span-1">
                                    <input 
                                        type="datetime-local" 
                                        required 
                                        className="w-full p-2.5 rounded-lg border dark:bg-dark-700 dark:border-dark-600 dark:text-white"
                                        value={formData.validUntil}
                                        onChange={(e) => setFormData({...formData, validUntil: e.target.value})}
                                    />
                                </div>
                                <div className="col-span-1">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Applicable Fee</label>
                                    <select 
                                        className="w-full p-2.5 rounded-lg border dark:bg-dark-700 dark:border-dark-600 dark:text-white"
                                        value={formData.applicableFee}
                                        onChange={(e) => setFormData({...formData, applicableFee: e.target.value})}
                                    >
                                        <option value="Our Fee">Our Fee</option>
                                        <option value="State Fee">State Fee</option>
                                        <option value="EIN Service">EIN Service</option>
                                        <option value="Registered Agent">Registered Agent</option>
                                        <option value="Total">Total</option>
                                    </select>
                                </div>
                                <div className="col-span-1">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Service Type</label>
                                    <select 
                                        className="w-full p-2.5 rounded-lg border dark:bg-dark-700 dark:border-dark-600 dark:text-white"
                                        value={formData.applicableService}
                                        onChange={(e) => setFormData({...formData, applicableService: e.target.value})}
                                    >
                                        <option value="All">All Services</option>
                                        <option value="USA Company Formation">USA Company Formation</option>
                                        <option value="Tax and Form Return (Starting)">Tax and Form Return (Starting)</option>
                                        <option value="Pay-As-You-Go">Pay-As-You-Go</option>
                                        <option value="Bookkeeping">Bookkeeping</option>
                                        <option value="CFO Advisory">CFO Advisory</option>
                                        <option value="Quick Payment">Quick Payment</option>
                                        <option value="Registered Agent">Registered Agent</option>
                                        <option value="Annual Report Service">Annual Report Service</option>
                                        <option value="Tax + Bookkeeping Bundle">Tax + Bookkeeping Bundle</option>
                                    </select>
                                </div>
                                <div className="col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                                    <textarea 
                                        className="w-full p-2.5 rounded-lg border dark:bg-dark-700 dark:border-dark-600 dark:text-white"
                                        placeholder="Brief description of the promo code"
                                        rows="2"
                                        value={formData.description}
                                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                                    ></textarea>
                                </div>
                            </div>
                            <div className="flex gap-3 mt-6">
                                <button 
                                    type="button" 
                                    onClick={handleCloseModal}
                                    className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit"
                                    className="flex-1 px-4 py-2.5 bg-primary-600 text-white font-medium rounded-xl hover:bg-primary-700 shadow-lg shadow-primary-500/20 transition-colors"
                                >
                                    {editingCode ? 'Save Changes' : 'Create Promo Code'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>,
                document.body
            )}
            {/* Details Modal */}
            {isDetailsModalOpen && selectedCodeDetails && createPortal(
                <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto" onClick={handleCloseDetailsModal}>
                    <div className="bg-white dark:bg-dark-800 rounded-2xl shadow-xl w-full max-w-2xl overflow-hidden animate-scale-in my-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="p-6 border-b border-gray-100 dark:border-dark-700 flex justify-between items-center">
                            <div>
                                <h3 className="text-xl font-bold dark:text-white flex items-center gap-2">
                                    <FaChartPie className="text-primary-500" /> Usage Details: {selectedCodeDetails.code}
                                </h3>
                                <p className="text-sm text-gray-500">{selectedCodeDetails.description}</p>
                            </div>
                            <button onClick={handleCloseDetailsModal} className="text-gray-400 hover:text-gray-600">
                                <FaTimes size={20} />
                            </button>
                        </div>
                        <div className="p-6 max-h-[70vh] overflow-y-auto space-y-8">
                            {/* Applied By Section */}
                            <div>
                                <h4 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-4 flex items-center gap-2">
                                    <FaUsers size={14} /> Applied By ({selectedCodeDetails.appliedBy?.length || 0})
                                </h4>
                                <div className="space-y-3">
                                    {selectedCodeDetails.appliedBy && selectedCodeDetails.appliedBy.length > 0 ? (
                                        selectedCodeDetails.appliedBy.map((app, index) => (
                                            <div key={index} className="flex justify-between items-center p-3 bg-gray-50 dark:bg-dark-700/50 rounded-xl border border-gray-100 dark:border-dark-700">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center text-primary-600 font-bold text-xs">
                                                        {app.user?.name?.charAt(0) || 'U'}
                                                    </div>
                                                    <div>
                                                        <div className="font-semibold text-sm dark:text-white">{app.user?.name || 'Unknown User'}</div>
                                                        <div className="text-xs text-gray-500">{app.user?.email || 'No email provided'}</div>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-xs text-gray-400">{new Date(app.date).toLocaleDateString()}</div>
                                                    <div className="text-[10px] text-gray-500">{new Date(app.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-4 text-gray-500 text-sm">No one has applied this code yet.</div>
                                    )}
                                </div>
                            </div>

                            {/* Converted By Section */}
                            <div>
                                <h4 className="text-sm font-semibold text-green-500 uppercase tracking-wider mb-4 flex items-center gap-2">
                                    <FaCheck size={14} /> Converted By ({selectedCodeDetails.convertedBy?.length || 0})
                                </h4>
                                <div className="space-y-3">
                                    {selectedCodeDetails.convertedBy && selectedCodeDetails.convertedBy.length > 0 ? (
                                        selectedCodeDetails.convertedBy.map((conv, index) => (
                                            <div key={index} className="flex justify-between items-center p-3 bg-green-50 dark:bg-green-900/10 rounded-xl border border-green-100 dark:border-green-900/20">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 font-bold text-xs">
                                                        {conv.user?.name?.charAt(0) || 'U'}
                                                    </div>
                                                    <div>
                                                        <div className="font-semibold text-sm dark:text-white">{conv.user?.name || 'Unknown User'}</div>
                                                        <div className="text-xs text-gray-500">Order: <span className="font-mono">{conv.orderId}</span></div>
                                                    </div>
                                                </div>
                                                <div className="text-right">
                                                    <div className="text-xs text-gray-400">{new Date(conv.date).toLocaleDateString()}</div>
                                                    <div className="text-[10px] text-gray-500">{new Date(conv.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-4 text-gray-500 text-sm">No conversions yet.</div>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="p-6 border-t border-gray-100 dark:border-dark-700">
                            <button 
                                onClick={handleCloseDetailsModal}
                                className="w-full px-4 py-2.5 bg-gray-100 dark:bg-dark-700 text-gray-600 dark:text-gray-400 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-dark-600 transition-colors"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </PageLayout>
    );
};

export default PromoCodeManagement;
